<script setup>
/**
 * HeroTimelineMockup — visual anchor for the hero.
 *
 * Replaces the generic ScreenshotPlaceholder with a stylised timeline
 * heatmap (pattern: Time Doctor) plus floating mini-cards around it
 * (pattern: Hubstaff collage). Goal: communicate the product's most
 * distinctive surface (WORK / IDLE / OFF spans across employees) in
 * one glance.
 *
 * Each row = one employee's day (08:00–18:00). Each cell ≈ 30 min.
 * Cells are not real data — they're a curated composition that LOOKS
 * like a healthy workday with realistic variance, so the visual reads
 * as "real product" without claiming any specific customer's metrics.
 *
 * The "now" indicator is a soft vertical bar with a pulsing dot.
 */
import { ref, onMounted, onUnmounted, computed } from "vue";
import { Activity, AlertCircle, TrendingUp } from "lucide-vue-next";

// 5 employees × 20 cells (08:00 → 18:00, 30-min slots).
// Cell types: 'w' = work, 'i' = idle, 'o' = off, 'b' = break (light idle).
// Curated to feel realistic: some early starters, some late, breaks at midday.
const rows = [
  { name: "Marta G.", role: "Diseño", cells: "wwwwwwbbwwwwwwwbbwww".split("") },
  { name: "Carlos R.", role: "Backend", cells: "owwwwwwwbbwwwwwwwwwo".split("") },
  { name: "Ana P.", role: "Soporte", cells: "wwwwwiwwbbwwwwwwwooo".split("") },
  { name: "Luis M.", role: "Ventas", cells: "wwwwwwwwbbwwiwwwwwwo".split("") },
  { name: "Sara T.", role: "Diseño", cells: "owwwwwwwbbwwwwwwwwwo".split("") },
];

const hours = ["08", "10", "12", "14", "16", "18"];

// Cell color map. We use accent (cyan) for WORK to keep brand consistency,
// muted shades for IDLE/BREAK/OFF so the eye scans WORK density first.
const cellClass = (c) => {
  switch (c) {
    case "w":
      return "bg-accent/85";
    case "b":
      return "bg-accent/30";
    case "i":
      return "bg-amber-500/60";
    case "o":
    default:
      return "bg-muted border border-border/40";
  }
};

// Pulsing "now" indicator advances slowly through the timeline so the
// visual feels alive without being noisy.
const nowIndex = ref(13);
let timer;
onMounted(() => {
  timer = setInterval(() => {
    nowIndex.value = (nowIndex.value + 1) % 20;
  }, 4000);
});
onUnmounted(() => clearInterval(timer));

const cellWidth = computed(() => `calc((100% - 19 * 0.25rem) / 20)`);
</script>

<template>
  <figure class="relative w-full">
    <!-- Floating mini-card top-left: live count of active employees -->
    <div
      class="absolute -top-4 -left-4 z-20 px-3.5 py-2.5 rounded-xl bg-card border border-border surface-elevated flex items-center gap-2.5 hidden md:flex"
    >
      <span class="relative flex h-2.5 w-2.5">
        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
        <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
      </span>
      <div>
        <p class="text-[10px] uppercase tracking-wider text-muted-foreground leading-none mb-0.5">
          Activos ahora
        </p>
        <p class="text-sm font-bold text-foreground leading-none">47 / 50</p>
      </div>
    </div>

    <!-- Floating mini-card top-right: productivity KPI with delta -->
    <div
      class="absolute -top-3 -right-2 z-20 px-3.5 py-2.5 rounded-xl bg-card border border-border surface-elevated hidden md:block"
    >
      <p class="text-[10px] uppercase tracking-wider text-muted-foreground leading-none mb-1.5">
        Productividad media
      </p>
      <div class="flex items-baseline gap-1.5">
        <span class="text-xl font-bold text-foreground leading-none">87%</span>
        <span class="text-[11px] font-semibold text-emerald-500 inline-flex items-center gap-0.5">
          <TrendingUp class="w-3 h-3" />
          +5
        </span>
      </div>
    </div>

    <!-- Floating alert card bottom-left: shows the alert system at work -->
    <div
      class="absolute -bottom-4 -left-2 z-20 px-3.5 py-2.5 rounded-xl bg-card border border-amber-500/30 surface-elevated flex items-center gap-2.5 hidden lg:flex"
    >
      <div class="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
        <AlertCircle class="w-3.5 h-3.5 text-amber-500" />
      </div>
      <div>
        <p class="text-[10px] uppercase tracking-wider text-muted-foreground leading-none mb-0.5">
          Alerta nueva
        </p>
        <p class="text-xs font-semibold text-foreground leading-tight">
          Inactividad &gt; 15min · Equipo Soporte
        </p>
      </div>
    </div>

    <!-- Main mockup card with the timeline heatmap -->
    <div class="relative mockup-frame p-5 lg:p-6">
      <!-- Header strip -->
      <div class="flex items-center justify-between mb-5 pb-4 border-b border-border">
        <div class="flex items-center gap-2">
          <Activity class="w-4 h-4 text-accent" />
          <span class="text-sm font-semibold text-foreground">Timeline · Hoy</span>
        </div>
        <div class="flex gap-1.5">
          <span class="w-2 h-2 rounded-full bg-accent/70" />
          <span class="w-2 h-2 rounded-full bg-amber-500/60" />
          <span class="w-2 h-2 rounded-full bg-muted border border-border/60" />
        </div>
      </div>

      <!-- Hour axis -->
      <div class="grid grid-cols-6 gap-0 mb-2 pl-[88px]">
        <span
          v-for="(h, i) in hours"
          :key="i"
          class="text-[10px] text-muted-foreground font-mono"
        >
          {{ h }}:00
        </span>
      </div>

      <!-- Employee rows -->
      <div class="space-y-2.5">
        <div
          v-for="(row, ri) in rows"
          :key="ri"
          class="flex items-center gap-3"
        >
          <!-- Avatar + name (truncated) -->
          <div class="flex items-center gap-2 w-[80px] flex-shrink-0">
            <div class="w-6 h-6 rounded-full bg-muted border border-border flex items-center justify-center text-[9px] font-bold text-foreground">
              {{ row.name.split(" ").map(s => s[0]).join("") }}
            </div>
            <div class="min-w-0">
              <p class="text-[10px] font-medium text-foreground leading-none truncate">
                {{ row.name.split(" ")[0] }}
              </p>
              <p class="text-[9px] text-muted-foreground leading-none mt-0.5 truncate">
                {{ row.role }}
              </p>
            </div>
          </div>

          <!-- Timeline cells row -->
          <div class="relative flex-1 flex gap-1">
            <span
              v-for="(c, ci) in row.cells"
              :key="ci"
              :class="['h-5 rounded-[3px] transition-colors duration-300', cellClass(c)]"
              :style="{ width: cellWidth }"
            />
          </div>
        </div>
      </div>

      <!-- "Now" indicator: vertical bar across all rows -->
      <div
        class="absolute pointer-events-none transition-all duration-1000 ease-out"
        :style="{
          left: `calc(${20 + 80 + 12}px + ${nowIndex} * ((100% - ${20 + 80 + 12}px - ${20}px - 19 * 0.25rem) / 20) + ${nowIndex} * 0.25rem)`,
          top: '76px',
          bottom: '20px',
          width: '2px',
        }"
      >
        <div class="w-full h-full bg-accent/40 rounded-full" />
        <span class="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-accent ring-2 ring-background" />
      </div>

      <!-- Footer mini-stats -->
      <div class="mt-5 pt-4 border-t border-border flex items-center justify-between text-[10px] text-muted-foreground">
        <span class="flex items-center gap-1.5">
          <span class="inline-block w-2 h-2 rounded-sm bg-accent/85" />
          Trabajo activo
        </span>
        <span class="flex items-center gap-1.5">
          <span class="inline-block w-2 h-2 rounded-sm bg-amber-500/60" />
          Inactividad detectada
        </span>
        <span class="flex items-center gap-1.5">
          <span class="inline-block w-2 h-2 rounded-sm bg-muted border border-border/60" />
          Fuera de turno
        </span>
      </div>
    </div>
  </figure>
</template>
